import type { Profile } from './user';
import type { AppTheme } from '@/utils/theme';
import { KIDS_THEME } from '@/utils/theme';
import { hasCompleteProfile, themeForAudience } from './user';

/**
 * The onboarding guards, as pure functions over the persisted profile. The
 * route modules wrap these in `clientLoader`s; keeping the decisions here is
 * what makes them testable without a router.
 *
 * A `null` return means "render this route".
 */

/** Where a guard sends the learner, or `null` to stay put. */
export type GuardRedirect = { to: string } | null;

/** The profile screen may also be told to seed the kids profile. */
export type ProfileGuardResult = { to: string } | { seedKids: true } | null;

const journeyPath = (profile: Profile) => `/h/${themeForAudience(profile.audience)}/learn/topics`;

/**
 * `/` — a returning learner who finished onboarding is not pitched twice; they
 * go straight to their journey.
 */
export function guardWelcome(profile: Profile | null): GuardRedirect {
  return profile && hasCompleteProfile(profile) ? { to: journeyPath(profile) } : null;
}

/**
 * The `/h/:theme` shell. No profile at all goes back to the welcome screen; a
 * profile without a name goes to the name screen.
 *
 * The name screen needs no exemption here: `/h/:theme/profile` lives under the
 * onboarding layout, not this shell, so this guard never runs for it.
 */
export function guardThemedShell({
  profile,
  theme,
}: {
  profile: Profile | null;
  theme: AppTheme;
}): GuardRedirect {
  if (!profile) {
    return { to: '/' };
  }

  if (profile.name === undefined || profile.name === '') {
    return { to: `/h/${theme}/profile` };
  }

  // Someone who switched audience — or followed an old kids link as an adult —
  // is moved onto the theme their audience belongs to, so the kids palette and
  // the kids content never disagree.
  const expected = themeForAudience(profile.audience);

  return theme === expected ? null : { to: `/h/${expected}/learn/topics` };
}

/**
 * `/h/:theme/profile`. With no profile, only the kids entry (`/h/beach`) may
 * seed one — everyone else has skipped the language and audience step and is
 * sent back to the welcome screen.
 */
export function guardProfileScreen({
  profile,
  theme,
}: {
  profile: Profile | null;
  theme: AppTheme;
}): ProfileGuardResult {
  if (profile) {
    return null;
  }

  return theme === KIDS_THEME ? { seedKids: true } : { to: '/' };
}

/**
 * A topic route. A slug that is not valid for this profile's audience and
 * language returns to the journey rather than showing content meant for
 * someone else.
 */
export function guardTopic({
  profile,
  validModuleSlugs,
  slug,
}: {
  profile: Profile | null;
  validModuleSlugs: Array<string>;
  slug: string;
}): GuardRedirect {
  if (!profile) {
    return { to: '/' };
  }

  return validModuleSlugs.includes(slug) ? null : { to: journeyPath(profile) };
}

/**
 * A reward screen — `/passport`, `/get-passport` and the certificate page.
 *
 * Nobody is shown a passport they have not earned: an unfinished course goes
 * back to the journey, and no profile at all to the welcome screen. `complete`
 * comes from `courseFor(state, rows).complete`, which is false while the topics
 * are still loading, so an empty list can never let someone through.
 */
export function guardCourseComplete({
  profile,
  complete,
}: {
  profile: Profile | null;
  complete: boolean;
}): GuardRedirect {
  if (!profile) {
    return { to: '/' };
  }

  return complete ? null : { to: journeyPath(profile) };
}
